import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCoins, faPlus, faCheckCircle, faClock, faTrash, faBan } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import ProviderShell, { useProviderApi } from './layouts/ProviderShell';

interface Invoice {
    id: number;
    invoice_number: string;
    description: string;
    amount: string;
    status: 'unpaid' | 'paid' | 'cancelled';
    due_date: string | null;
    paid_date: string | null;
    issued_date: string;
    patient: { patient_id: string; name: string };
}

interface PatientOption {
    patient_id: string;
    first_name: string;
    last_name: string;
}

interface Summary {
    total_earned: string;
    total_outstanding: string;
    paid_count: number;
    unpaid_count: number;
    total_invoices: number;
}

const STATUS_COLORS: Record<string, string> = { unpaid: 'warning', paid: 'success', cancelled: 'secondary' };

const ProviderBilling: React.FC = () => {
    const { api } = useProviderApi();

    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [summary, setSummary] = useState<Summary | null>(null);
    const [patients, setPatients] = useState<PatientOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('');

    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState({ patient_id: '', description: '', amount: '', due_date: '' });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => { document.title = 'Billing & Earnings - Uzima Healthcare'; }, []);

    const loadAll = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statusFilter) params.set('status', statusFilter);
            const [invRes, sumRes, patRes] = await Promise.all([
                api.get(`practitioners/invoices/?${params}`),
                api.get('practitioners/earnings-summary/'),
                api.get('practitioners/patients/?status=active'),
            ]);
            if (invRes.data?.status === 'success') setInvoices(invRes.data.data || []);
            if (sumRes.data?.status === 'success') setSummary(sumRes.data.data);
            if (patRes.data?.status === 'success') setPatients(patRes.data.data.map((p: any) => ({ patient_id: p.patient_id, first_name: p.first_name, last_name: p.last_name })));
        } catch { /* leave state as-is on failure */ } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadAll(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    const submitInvoice = async () => {
        if (!form.patient_id || !form.description.trim() || !form.amount) {
            Swal.fire({ icon: 'warning', title: 'Missing Fields', text: 'Patient, description and amount are required.' });
            return;
        }
        setSubmitting(true);
        try {
            const res = await api.post('practitioners/invoices/', form);
            if (res.data?.status === 'success') {
                Swal.fire({ icon: 'success', title: 'Invoice Created', timer: 1500, showConfirmButton: false });
                setShowCreate(false);
                setForm({ patient_id: '', description: '', amount: '', due_date: '' });
                loadAll();
            }
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Failed to create invoice.' });
        } finally {
            setSubmitting(false);
        }
    };

    const markPaid = async (inv: Invoice) => {
        try {
            await api.patch(`practitioners/invoices/${inv.id}/`, { status: 'paid' });
            loadAll();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to update invoice.' });
        }
    };

    const cancelInvoice = async (inv: Invoice) => {
        const confirm = await Swal.fire({ title: 'Cancel this invoice?', icon: 'warning', showCancelButton: true, confirmButtonColor: '#dc3545' });
        if (!confirm.isConfirmed) return;
        try {
            await api.patch(`practitioners/invoices/${inv.id}/`, { status: 'cancelled' });
            loadAll();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to cancel invoice.' });
        }
    };

    const deleteInvoice = async (inv: Invoice) => {
        const confirm = await Swal.fire({ title: 'Delete this invoice?', icon: 'warning', showCancelButton: true, confirmButtonColor: '#dc3545' });
        if (!confirm.isConfirmed) return;
        try {
            await api.delete(`practitioners/invoices/${inv.id}/`);
            loadAll();
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Failed to delete invoice.' });
        }
    };

    return (
        <ProviderShell>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faCoins} className="me-2 text-primary" />Billing & Earnings</h2>
                    <p className="text-muted mb-0">Invoice your patients and track your earnings</p>
                </div>
                <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
                    <FontAwesomeIcon icon={faPlus} className="me-2" />New Invoice
                </button>
            </div>

            {summary && (
                <div className="row mb-4">
                    <div className="col-md-3">
                        <div className="card border-0 shadow-sm">
                            <div className="card-body">
                                <p className="text-muted small mb-1">Total Earned</p>
                                <h4 className="text-success mb-0">KES {Number(summary.total_earned).toLocaleString()}</h4>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card border-0 shadow-sm">
                            <div className="card-body">
                                <p className="text-muted small mb-1">Outstanding</p>
                                <h4 className="text-warning mb-0">KES {Number(summary.total_outstanding).toLocaleString()}</h4>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card border-0 shadow-sm">
                            <div className="card-body">
                                <p className="text-muted small mb-1">Paid Invoices</p>
                                <h4 className="mb-0">{summary.paid_count}</h4>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card border-0 shadow-sm">
                            <div className="card-body">
                                <p className="text-muted small mb-1">Total Invoices</p>
                                <h4 className="mb-0">{summary.total_invoices}</h4>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="mb-3">
                <select className="form-select" style={{ maxWidth: 220 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="">All Statuses</option>
                    <option value="unpaid">Unpaid</option>
                    <option value="paid">Paid</option>
                    <option value="cancelled">Cancelled</option>
                </select>
            </div>

            <div className="card">
                <div className="card-body p-0">
                    {loading ? (
                        <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                    ) : invoices.length === 0 ? (
                        <div className="text-center py-5">
                            <FontAwesomeIcon icon={faCoins} className="text-muted mb-3" style={{ fontSize: '2.5rem' }} />
                            <h5>No invoices yet</h5>
                            <p className="text-muted">Create your first invoice for a patient on your roster.</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="table-light">
                                    <tr><th>Invoice #</th><th>Patient</th><th>Description</th><th>Amount</th><th>Issued</th><th>Status</th><th>Action</th></tr>
                                </thead>
                                <tbody>
                                    {invoices.map(inv => (
                                        <tr key={inv.id}>
                                            <td><span className="badge bg-light text-dark">{inv.invoice_number}</span></td>
                                            <td>{inv.patient.name}</td>
                                            <td>{inv.description}</td>
                                            <td>KES {Number(inv.amount).toLocaleString()}</td>
                                            <td>{new Date(inv.issued_date).toLocaleDateString()}</td>
                                            <td><span className={`badge bg-${STATUS_COLORS[inv.status]}`}>{inv.status}</span></td>
                                            <td>
                                                <div className="d-flex gap-1">
                                                    {inv.status === 'unpaid' && (
                                                        <>
                                                            <button className="btn btn-sm btn-outline-success" onClick={() => markPaid(inv)} title="Mark Paid"><FontAwesomeIcon icon={faCheckCircle} /></button>
                                                            <button className="btn btn-sm btn-outline-secondary" onClick={() => cancelInvoice(inv)} title="Cancel"><FontAwesomeIcon icon={faBan} /></button>
                                                        </>
                                                    )}
                                                    {inv.status !== 'paid' && (
                                                        <button className="btn btn-sm btn-outline-danger" onClick={() => deleteInvoice(inv)} title="Delete"><FontAwesomeIcon icon={faTrash} /></button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {showCreate && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">New Invoice</h5>
                                <button type="button" className="btn-close" onClick={() => setShowCreate(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Patient</label>
                                    <select className="form-select" value={form.patient_id} onChange={(e) => setForm(f => ({ ...f, patient_id: e.target.value }))}>
                                        <option value="">Select a patient...</option>
                                        {patients.map(p => <option key={p.patient_id} value={p.patient_id}>{p.first_name} {p.last_name}</option>)}
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Description</label>
                                    <input type="text" className="form-control" placeholder="e.g. Consultation fee - 28 Jul" value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} />
                                </div>
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Amount (KES)</label>
                                        <input type="number" min={1} className="form-control" value={form.amount} onChange={(e) => setForm(f => ({ ...f, amount: e.target.value }))} />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Due Date</label>
                                        <input type="date" className="form-control" value={form.due_date} onChange={(e) => setForm(f => ({ ...f, due_date: e.target.value }))} />
                                    </div>
                                </div>
                                {patients.length === 0 && <div className="form-text text-warning mt-2">No patients on your roster yet — add one under My Patients first.</div>}
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setShowCreate(false)}>Cancel</button>
                                <button className="btn btn-primary" disabled={submitting} onClick={submitInvoice}>{submitting ? 'Creating...' : 'Create Invoice'}</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </ProviderShell>
    );
};

export default ProviderBilling;
